import * as React from "react";

// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import { Icon } from "@fluentui/react";
import { slot } from "@fluentui/react-components";

import type { AppButtonProps } from "../../AppButton/next";
import { useAppButtonState } from "../../AppButton/next";
import {
  AppIcon,
  getDownChevronIcon,
  iconNameToIconProps,
} from "@1js/acui-icons";
import type { AppMenuButtonProps, AppMenuButtonState } from ".";
import { AppContext } from "@1js/acui-context";
import { checkExperiment, ribbonExperiments } from "@1js/acui-experiments";

export const useAppMenuButtonState = (
  props: AppMenuButtonProps,
  state: AppMenuButtonState
): AppMenuButtonState => {
  const { onClick, onKeyDown, ariaLabel, labelInMenu, label, disabled } = props;
  // Fill in iconPosition because that is the only thing MenuButtonState doesn't have
  // that ButtonState has
  const appButtonState = useAppButtonState(props as AppButtonProps, {
    ...state,
    iconPosition: "before",
  });

  // Fill in missing menuIcon component as "span" (Fluent default)
  const appMenuButtonState: AppMenuButtonState = {
    ...appButtonState,
    components: {
      ...appButtonState.components,
      menuIcon: "span",
    },
  };

  const context = React.useContext(AppContext);
  const isV9IconEnabled = !!checkExperiment(
    context,
    ribbonExperiments.fluentV9IconIsEnabled
  );

  // Override properties that get set in useAppButtonState
  // and need to be preserved from the props because they were set by menu code
  const onMenuClick = React.useCallback(
    (
      ev: React.MouseEvent<HTMLButtonElement & HTMLAnchorElement, MouseEvent>
    ) => {
      if (disabled) {
        return;
      }
      onClick?.(ev);
    },
    [onClick, disabled]
  );
  appMenuButtonState.root.onClick = onMenuClick;

  const onMenuKeyDown = React.useCallback(
    (
      ev: React.KeyboardEvent<HTMLButtonElement> &
        React.KeyboardEvent<HTMLAnchorElement>
    ) => {
      if (disabled) {
        return;
      }
      onKeyDown?.(ev);
    },
    [onKeyDown, disabled]
  );
  appMenuButtonState.root.onKeyDown = onMenuKeyDown;

  appMenuButtonState.root["aria-label"] = ariaLabel || labelInMenu || label;

  // App Menu Button specific properties
  appMenuButtonState.menuHidden = props.menuHidden;

  const downChevronIcon = getDownChevronIcon(props);
  appMenuButtonState.menuIcon = slot.optional(
    downChevronIcon
      ? {
          children: isV9IconEnabled ? (
            <AppIcon iconName={downChevronIcon} />
          ) : (
            <Icon {...iconNameToIconProps(downChevronIcon)} />
          ),
          className: state.menuIcon?.className,
          style: state.menuIcon?.style,
        }
      : undefined,
    { elementType: "span" }
  );

  appMenuButtonState.root["aria-haspopup"] = true;

  return appMenuButtonState;
};
